import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ChevronDown,
  FileSpreadsheet,
  FileUp,
  FolderUp,
  GripVertical,
  Library,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import {
  checkImported,
  checkIsbnImported,
  CsvImportRow,
  fetchAmazonMetadata,
  getAllowedExtensions,
  getImportTags,
  ImportEvent,
  ImportProductSearchResult,
  ImportTag,
  searchAmazonTopHit,
  searchImportProducts,
  streamImportCsv,
  streamImportUpload,
} from '@/api/services/import'
import { requestProductOcr } from '@/api/services/ocr'
import type { LibraryFavorite } from '@/api/types/library-favorite'
import { Header } from '@/components/shared/header'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { getDomainLabel } from '@/lib/domain-labels'
import { buildAmazonUrl } from '@/pages/library-search/lib/amazon-url'
import { PATHS } from '@/routes/paths'
import { useAppStore } from '@/store/useAppStore'
import { useLibraryFavoritesStore } from '@/store/useLibraryFavoritesStore'

import { LibraryReservedSheet } from './components/library-reserved-sheet'

// インポート対応ドメイン
const IMPORTABLE_DOMAINS = [
  'local',
  'private',
  'audiobook',
  'english',
  'fod',
  'abema',
  'jcom',
  'unext',
  'hulu',
  'tver',
  'x',
  'miss',
  'monsnode',
  'zozo',
  'nh',
  'kadokomi',
  'oreilly',
  'apple_music',
] as const

const TAG_TYPES = ['store', 'author', 'category', 'genre', 'publisher', 'series', 'tag', 'album', 'narrator'] as const

type FileEntryStatus = 'pending' | 'done' | 'skipped' | 'error' | 'imported'

type FileStatus = { status: FileEntryStatus; message?: string }

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

const getExt = (name: string): string => {
  const idx = name.lastIndexOf('.')
  return idx >= 0 ? name.slice(idx).toLowerCase() : ''
}

// ファイルを一意に識別するキー
const fileKey = (file: File): string => `${file.name}_${file.size}`

// ファイルの並び順比較用パス（ディレクトリ選択時は相対パス、それ以外はファイル名）
const filePathForSort = (file: File): string =>
  (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name

// 数字を考慮した自然順ソート用コレーター
const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

// CSVの1行（カンマ区切り・ダブルクォート対応）を列配列へ分解する
const parseCsvLine = (line: string): string[] => {
  const result: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        cur += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      result.push(cur)
      cur = ''
    } else {
      cur += ch
    }
  }
  result.push(cur)
  return result
}

// CSVテキストをインポート行へ変換する（1列目=Path, 2列目=カスタム名, 3列目=日付）
const parseCsvText = (text: string): CsvImportRow[] => {
  const clean = text.replace(/^\uFEFF/, '')
  const lines = clean.split(/\r?\n/).filter((l) => l.trim().length > 0)
  const rows: CsvImportRow[] = []
  for (let i = 0; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i])
    const path = (cols[0] ?? '').trim()
    // 先頭行がヘッダー（Path）の場合はスキップ
    if (i === 0 && path.toLowerCase() === 'path') continue
    if (!path) continue
    const customName = (cols[1] ?? '').trim()
    const uploadDate = (cols[2] ?? '').trim()
    rows.push({
      path,
      customName: customName || null,
      uploadDate: uploadDate || null,
    })
  }
  return rows
}

// FileSystemEntry を再帰的に走査してファイルを収集（ディレクトリDnD対応）
const readEntryFiles = async (entry: FileSystemEntry): Promise<File[]> => {
  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry
    return new Promise((resolve) => {
      fileEntry.file(
        (file) => resolve([file]),
        () => resolve([]),
      )
    })
  }
  const dirEntry = entry as FileSystemDirectoryEntry
  const reader = dirEntry.createReader()
  const entries: FileSystemEntry[] = await new Promise((resolve) => {
    const all: FileSystemEntry[] = []
    const readBatch = () => {
      reader.readEntries(
        (batch) => {
          if (batch.length === 0) {
            resolve(all)
            return
          }
          all.push(...batch)
          readBatch()
        },
        () => resolve(all),
      )
    }
    readBatch()
  })
  const nested = await Promise.all(entries.map(readEntryFiles))
  return nested.flat()
}

// ソート可能なファイル行
const SortableFileItem = ({
  file,
  status,
  valid,
  importing,
  isNew,
  onRemove,
}: {
  file: File
  status: FileStatus | undefined
  valid: boolean
  importing: boolean
  isNew: boolean
  onRemove: (key: string) => void
}) => {
  const key = fileKey(file)
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: key,
    disabled: importing,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-file-key={key}
      className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors duration-500 ${
        isNew ? 'bg-primary/15' : 'bg-background'
      } ${isDragging ? 'opacity-50' : ''}`}
    >
      <div
        className={`shrink-0 touch-none text-muted-foreground/50 ${
          importing ? 'opacity-20' : 'cursor-grab active:cursor-grabbing'
        }`}
        {...(importing ? {} : { ...attributes, ...listeners })}
      >
        <GripVertical size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`truncate ${valid ? '' : 'text-destructive'}`}>{file.name}</p>
        <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
      </div>
      {status?.status === 'done' && <Badge variant="secondary">登録</Badge>}
      {status?.status === 'imported' && <Badge variant="outline">インポート済み</Badge>}
      {status?.status === 'skipped' && <Badge variant="outline">スキップ</Badge>}
      {status?.status === 'error' && <Badge variant="destructive">失敗</Badge>}
      {!status && isNew && <Badge variant="default">追加</Badge>}
      {status?.status === 'pending' && importing && (
        <Loader2 size={14} className="animate-spin text-muted-foreground" />
      )}
      {!valid && !status && <Badge variant="destructive">非対応</Badge>}
      {!importing && (
        <button onClick={() => onRemove(key)} aria-label="削除">
          <X size={14} className="text-muted-foreground" />
        </button>
      )}
    </div>
  )
}

export const ImportPage = () => {
  const navigate = useNavigate()
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)
  const authSyncStatus = useAppStore((state) => state.authSyncStatus)
  const updateFavoriteStatus = useLibraryFavoritesStore((state) => state.updateStatus)

  const [mode, setMode] = useState<'new' | 'append'>('new')
  const [domain, setDomain] = useState<string>('local')
  const [title, setTitle] = useState('')
  const [releaseDate, setReleaseDate] = useState('')
  const [description, setDescription] = useState('')
  const [tags, setTags] = useState<ImportTag[]>([])
  const [newTagName, setNewTagName] = useState('')
  const [newTagType, setNewTagType] = useState<string>('store')
  const [existingTags, setExistingTags] = useState<ImportTag[]>([])
  const [files, setFilesState] = useState<File[]>([])
  // 追加直後に「何件増えたか」を同期的に判定したいので最新値をrefでも持つ
  const filesRef = useRef<File[]>([])
  const setFiles = useCallback((next: File[] | ((prev: File[]) => File[])) => {
    const value = typeof next === 'function' ? next(filesRef.current) : next
    filesRef.current = value
    setFilesState(value)
  }, [])
  // 追加されたばかりのファイル（一定時間ハイライトする）
  const [recentKeys, setRecentKeys] = useState<string[]>([])
  const fileListRef = useRef<HTMLDivElement>(null)
  const [csvRows, setCsvRows] = useState<CsvImportRow[]>([])
  const [csvName, setCsvName] = useState<string>('')
  const [thumbnail, setThumbnail] = useState<File | null>(null)
  const [allowedExts, setAllowedExts] = useState<string[] | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  // Amazon URL からのAI取得
  const [amazonUrl, setAmazonUrl] = useState('')
  const [fetchingMeta, setFetchingMeta] = useState(false)
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null)

  // 自動モード（ファイル選択時にAmazon検索 → 確認 → AI解析）
  const [autoMode, setAutoMode] = useState(false)
  const [autoSearching, setAutoSearching] = useState(false)
  const [autoConfirm, setAutoConfirm] = useState<{ keyword: string; productUrl: string } | null>(null)

  // 図書館予約 (Calil) 由来のインポート:
  // - 選択された時点で domain=local に固定し、`libraryIsbn` に正規化 ISBN を保持する
  // - libraryIsbn が非 null の間はドメインセレクタを無効化する
  // - streamImportUpload には `isbn` として渡し、バックエンドが domain_product_id に使う
  const [libraryReservedOpen, setLibraryReservedOpen] = useState(false)
  const [libraryIsbn, setLibraryIsbn] = useState<string | null>(null)
  const [libraryChecking, setLibraryChecking] = useState(false)

  // append モード
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<ImportProductSearchResult[]>([])
  const [targetProduct, setTargetProduct] = useState<ImportProductSearchResult | null>(null)

  // インポート完了後に Yomitoku で再OCRするか
  const [runOcrAfterImport, setRunOcrAfterImport] = useState(true)

  // インポート進捗
  const { isSubmitting: importing, run } = useSubmitLock()
  const [fileStatuses, setFileStatuses] = useState<Record<string, FileStatus>>({})
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const dirInputRef = useRef<HTMLInputElement>(null)
  const csvInputRef = useRef<HTMLInputElement>(null)
  // addFiles から最新の runAutoSearch を参照するための ref（定義順依存を回避）
  const runAutoSearchRef = useRef<(filename: string) => void>(() => {})

  // フルアクセス権限がない場合はホームへ
  // /users/me の取得が完了する前（idle / loading）や失敗時はリダイレクトしない
  useEffect(() => {
    if (authSyncStatus !== 'success') return
    if (!hasFullAccess) {
      navigate(PATHS.HOME)
    }
  }, [authSyncStatus, hasFullAccess, navigate])

  // ディレクトリ選択用 input に webkitdirectory 属性を付与
  useEffect(() => {
    dirInputRef.current?.setAttribute('webkitdirectory', '')
    dirInputRef.current?.setAttribute('directory', '')
  }, [])

  // ドメイン変更時に許可拡張子を取得
  useEffect(() => {
    let active = true
    getAllowedExtensions(domain)
      .then((res) => {
        if (active) setAllowedExts(res.extensions)
      })
      .catch(() => {
        if (active) setAllowedExts(null)
      })
    return () => {
      active = false
    }
  }, [domain])

  // ドメイン変更時に既存タグを取得（補完候補用）
  useEffect(() => {
    let active = true
    getImportTags(domain)
      .then((res) => {
        if (active) setExistingTags(res)
      })
      .catch(() => {
        if (active) setExistingTags([])
      })
    return () => {
      active = false
    }
  }, [domain])

  // append モードで対象Productに既にインポート済みのファイルを判定
  useEffect(() => {
    if (mode !== 'append' || !targetProduct || files.length === 0) return
    let active = true
    const filenames = Array.from(new Set(files.map((f) => f.name)))
    checkImported(domain, targetProduct.domain_product_id, filenames)
      .then((imported) => {
        if (!active || imported.length === 0) return
        const importedSet = new Set(imported)
        setFileStatuses((prev) => {
          const next = { ...prev }
          for (const f of files) {
            if (importedSet.has(f.name)) {
              next[`${f.name}_${f.size}`] = { status: 'imported' }
            }
          }
          return next
        })
      })
      .catch(() => {
        // 判定失敗時は無視（インポート時に最終判定される）
      })
    return () => {
      active = false
    }
  }, [mode, domain, targetProduct, files])

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return
      // 追加分を自然順（数字考慮）でソートしてから登録
      const sortedIncoming = [...incoming].sort((a, b) =>
        naturalCollator.compare(filePathForSort(a), filePathForSort(b)),
      )
      const prev = filesRef.current
      const seen = new Set(prev.map(fileKey))
      const merged = [...prev]
      const addedKeys: string[] = []
      let firstNewName: string | null = null
      for (const f of sortedIncoming) {
        const key = fileKey(f)
        if (seen.has(key)) continue
        seen.add(key)
        merged.push(f)
        addedKeys.push(key)
        if (firstNewName === null) firstNewName = f.name
      }
      setFiles(merged)

      if (addedKeys.length === 0) {
        toast.info('すべて追加済みのファイルです')
      } else {
        setRecentKeys(addedKeys)
        const duplicated = sortedIncoming.length - addedKeys.length
        const suffix = duplicated > 0 ? ` / 重複${duplicated}件は除外` : ''
        toast.success(`${addedKeys.length}件を追加しました（計${merged.length}件）${suffix}`)
      }
      // ディレクトリ選択時はフォルダ名をタイトル候補に
      const withPath = incoming.find((f) => (f as File & { webkitRelativePath?: string }).webkitRelativePath)
      if (withPath && !title) {
        const rel = (withPath as File & { webkitRelativePath?: string }).webkitRelativePath || ''
        const folder = rel.split('/')[0]
        if (folder) setTitle(folder)
      }
      // 自動モード: 新規ファイルの先頭名でAmazon検索を起動
      if (autoMode && mode === 'new' && firstNewName) {
        runAutoSearchRef.current(firstNewName)
      }
      // 通常ファイル選択時はCSV指定を解除（同時利用しない）
      setCsvRows([])
      setCsvName('')
    },
    [title, autoMode, mode, setFiles],
  )

  // 追加したファイルを一覧内で見える位置へ送り、ハイライトを一定時間で消す
  useEffect(() => {
    const first = recentKeys.at(0)
    if (!first) return
    const el = fileListRef.current?.querySelector<HTMLElement>(`[data-file-key="${window.CSS.escape(first)}"]`)
    el?.scrollIntoView({ block: 'nearest' })
    const timer = window.setTimeout(() => setRecentKeys([]), 2500)
    return () => window.clearTimeout(timer)
  }, [recentKeys])

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(Array.from(e.target.files))
    e.target.value = ''
  }

  const handleCsvInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const text = await file.text()
    const rows = parseCsvText(text)
    if (rows.length === 0) {
      toast.error('CSVから有効な行を読み取れませんでした')
      return
    }
    // CSV指定時は通常ファイル選択を解除（同時利用しない）
    setFiles([])
    setFileStatuses({})
    setCsvRows(rows)
    setCsvName(file.name)
  }

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      const items = e.dataTransfer.items
      if (items && items.length > 0 && typeof items[0].webkitGetAsEntry === 'function') {
        const entries: FileSystemEntry[] = []
        for (let i = 0; i < items.length; i++) {
          const entry = items[i].webkitGetAsEntry()
          if (entry) entries.push(entry)
        }
        const nested = await Promise.all(entries.map(readEntryFiles))
        addFiles(nested.flat())
        return
      }
      addFiles(Array.from(e.dataTransfer.files))
    },
    [addFiles],
  )

  const removeFile = (key: string) => {
    setFiles((prev) => prev.filter((f) => fileKey(f) !== key))
  }

  // ファイル並び替え（DnD）
  const fileSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const handleFileDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setFiles((prev) => {
      const oldIndex = prev.findIndex((f) => fileKey(f) === active.id)
      const newIndex = prev.findIndex((f) => fileKey(f) === over.id)
      if (oldIndex < 0 || newIndex < 0) return prev
      return arrayMove(prev, oldIndex, newIndex)
    })
  }

  const addTag = () => {
    const name = newTagName.trim()
    if (!name) return
    if (tags.some((t) => t.name === name && t.type === newTagType)) {
      setNewTagName('')
      return
    }
    setTags((prev) => [...prev, { name, type: newTagType }])
    setNewTagName('')
  }

  const removeTag = (index: number) => {
    setTags((prev) => prev.filter((_, i) => i !== index))
  }

  // 選択中タイプに一致する既存タグ名（重複・追加済みを除外）
  const tagNameSuggestions = Array.from(
    new Set(
      existingTags
        .filter((t) => t.type === newTagType)
        .map((t) => t.name)
        .filter((name) => !tags.some((added) => added.type === newTagType && added.name === name)),
    ),
  )

  const handleFetchAmazon = useCallback(
    async (overrideUrl?: string) => {
      const url = (overrideUrl ?? amazonUrl).trim()
      if (!url) return
      setFetchingMeta(true)
      try {
        const meta = await fetchAmazonMetadata(url)
        if (meta.title) setTitle(meta.title)
        if (meta.tags.length > 0) {
          setTags((prev) => {
            const seen = new Set(prev.map((t) => `${t.type}_${t.name}`))
            const merged = [...prev]
            for (const tag of meta.tags) {
              const key = `${tag.type}_${tag.name}`
              if (!seen.has(key)) {
                seen.add(key)
                merged.push(tag)
              }
            }
            return merged
          })
        }
        setThumbnailUrl(meta.thumbnail_url)
        if (meta.release_date) setReleaseDate(meta.release_date)
        if (meta.description) setDescription(meta.description)
        toast.success('AIでメタデータを取得しました')
      } catch {
        toast.error('Amazonメタデータの取得に失敗しました')
      } finally {
        setFetchingMeta(false)
      }
    },
    [amazonUrl],
  )

  // 自動モード: ファイル名でAmazon検索し、外部ブラウザで開いて確認ダイアログを表示
  const runAutoSearch = useCallback(async (filename: string) => {
    // 拡張子を除き、末尾が _EBOOK の場合はそれも除いて検索する
    const keyword = filename
      .replace(/\.[^/.]+$/, '')
      .replace(/_EBOK$/i, '')
      .trim()
    if (!keyword) return
    setAutoSearching(true)
    try {
      const result = await searchAmazonTopHit(keyword)
      // 別ウィンドウ（ポップアップ）で開く（商品が見つかればその商品、無ければ検索結果ページ）
      const targetUrl = result.product_url ?? result.search_url
      window.open(targetUrl, 'amazonSearchWindow', 'popup=yes,noopener,noreferrer,width=900,height=800')
      if (!result.product_url) {
        toast.warning('一致する商品が見つかりませんでした。検索結果を開きました')
        return
      }
      setAutoConfirm({ keyword, productUrl: result.product_url })
    } catch {
      toast.error('Amazon検索に失敗しました')
    } finally {
      setAutoSearching(false)
    }
  }, [])

  useEffect(() => {
    runAutoSearchRef.current = runAutoSearch
  }, [runAutoSearch])

  /**
   * 図書館予約 (reserved) から書籍を選択したときのハンドラ。
   *
   * 1. ISBN で `local` ドメインに既存 Product があるかバックエンドに問い合わせる
   * 2. 既存があればブロックしてトースト通知 (要件どおり)
   * 3. 無ければ:
   *    - domain='local' / mode='new' に強制
   *    - `store:scan` と `store:library` タグを未追加なら追加
   *    - `libraryIsbn` を保持 (import 送信時に isbn として同梱)
   *    - Amazon URL を組み立てて AI 解析を発火
   */
  const handleLibrarySelect = useCallback(
    async (fav: LibraryFavorite) => {
      const isbn = fav.isbn
      if (!isbn) {
        toast.error('ISBN が無い書籍は選択できません')
        return
      }
      setLibraryChecking(true)
      try {
        const check = await checkIsbnImported(isbn)
        if (check.exists) {
          toast.error(`既に自炊済みです: ${check.title ?? fav.title}`)
          setLibraryReservedOpen(false)
          return
        }
      } catch (err) {
        // ネットワーク等で判定に失敗した場合はシートを開いたまま (ユーザーが再選択可能)
        console.warn('checkIsbnImported failed', err)
        toast.error('自炊済み判定に失敗しました')
        return
      } finally {
        setLibraryChecking(false)
      }

      // ドメイン・モード確定 (local / new)
      setDomain('local')
      setMode('new')
      setLibraryIsbn(isbn)
      setLibraryReservedOpen(false)

      // タグ自動付与 (store:scan + store:library)。既存重複は上書きしない
      const libraryAutoTags: ImportTag[] = [
        { type: 'store', name: 'scan' },
        { type: 'store', name: 'library' },
      ]
      setTags((prev) => {
        const merged = [...prev]
        const seen = new Set(prev.map((t) => `${t.type}_${t.name}`))
        for (const tag of libraryAutoTags) {
          const key = `${tag.type}_${tag.name}`
          if (!seen.has(key)) {
            seen.add(key)
            merged.push(tag)
          }
        }
        return merged
      })

      // Amazon URL 生成 → AI 解析発火 (共通ヘルパー使用: 978 → ISBN-10 変換 / 979 → 検索 / タイトル fallback)
      const url = buildAmazonUrl(isbn, fav.title)
      if (!url) {
        toast.warning('Amazon URL を組み立てられませんでした')
        return
      }
      setAmazonUrl(url)
      await handleFetchAmazon(url)
    },
    [handleFetchAmazon],
  )

  /** 図書館予約由来のインポートを解除する (通常の new モードに戻す) */
  const clearLibrarySelection = useCallback(() => {
    setLibraryIsbn(null)
    // タグは残す (ユーザーが意図的に外したい場合は手動で削除できる)
  }, [])

  const handleSearch = useCallback(async () => {
    try {
      const results = await searchImportProducts(domain, searchQuery)
      setSearchResults(results)
    } catch {
      toast.error('Product検索に失敗しました')
    }
  }, [domain, searchQuery])

  const isExtAllowed = (name: string): boolean => {
    if (allowedExts === null) return true
    return allowedExts.includes(getExt(name))
  }

  const invalidFiles = files.filter((f) => !isExtAllowed(f.name))

  // 再OCR は PDF エピソードのみが対象なので、PDF を含むときだけオプションを出す
  const hasPdfTarget =
    files.some((f) => getExt(f.name) === '.pdf') || csvRows.some((row) => getExt(row.path) === '.pdf')

  const canImport = (): boolean => {
    if (importing) return false
    if (files.length === 0 && csvRows.length === 0) return false
    if (mode === 'new') return title.trim().length > 0
    return targetProduct !== null
  }

  // 入力フォーム全体を初期状態へリセットする
  const handleClearAll = () => {
    setFiles([])
    setCsvRows([])
    setCsvName('')
    setFileStatuses({})
    setTitle('')
    setReleaseDate('')
    setDescription('')
    setAmazonUrl('')
    setThumbnail(null)
    setThumbnailUrl(null)
    setTags([])
    setNewTagName('')
    setSearchQuery('')
    setSearchResults([])
    setTargetProduct(null)
    setLibraryIsbn(null)
  }

  // 取り込みが済んだ本はもう手元の予約に残しておく必要がないので「履歴」へ送る
  const markLibraryBookReturned = async () => {
    if (!libraryIsbn) return
    const ok = await updateFavoriteStatus(libraryIsbn, 'returned')
    if (!ok) {
      toast.error('図書館の予約状態を履歴に移せませんでした')
      return
    }
    toast.info('予約中の本を履歴に移しました')
  }

  // インポートした作品の PDF を Yomitoku で再OCRするジョブを積む
  const queueOcrForProduct = async (productId: number | null) => {
    if (!runOcrAfterImport) return
    if (productId === null) {
      toast.error('再OCR の対象作品を特定できませんでした')
      return
    }
    try {
      await requestProductOcr(productId)
      toast.success('再OCR をキューに登録しました')
    } catch {
      toast.error('再OCR の登録に失敗しました')
    }
  }

  // CSVによるローカルパス一括インポート
  // handleImport から呼ばれるので、二重送信ロックは呼び出し元でかける
  const handleCsvImport = async () => {
    setImportProgress({ current: 0, total: csvRows.length })
    let registered = 0
    let skipped = 0
    let failed = 0
    let productId: number | null = null
    const bump = () => setImportProgress({ current: registered + skipped + failed, total: csvRows.length })

    await streamImportCsv(
      {
        domain,
        title: title.trim(),
        tags,
        mode,
        targetDomainProductId: mode === 'append' ? targetProduct?.domain_product_id : undefined,
        description: description || null,
        rows: csvRows,
      },
      (event) => {
        if (event.status === 'file_done') {
          registered += 1
          bump()
        } else if (event.status === 'file_skipped') {
          skipped += 1
          bump()
        } else if (event.status === 'file_error') {
          failed += 1
          bump()
        } else if (event.status === 'start' || event.status === 'done') {
          productId = event.product_id
        } else if (event.status === 'error') {
          failed += 1
          toast.error(event.message)
        }
      },
      (err) => {
        failed += 1
        toast.error(err.message)
      },
    )

    toast.success(`登録 ${registered} / スキップ ${skipped} / 失敗 ${failed}`)
    if (failed === 0) {
      await markLibraryBookReturned()
      await queueOcrForProduct(productId)
      setCsvRows([])
      setCsvName('')
      setTitle('')
      setReleaseDate('')
      setDescription('')
      setAmazonUrl('')
      setThumbnail(null)
      setThumbnailUrl(null)
      setTags((prev) => prev.filter((t) => t.type === 'store'))
      setLibraryIsbn(null)
    }
    setImportProgress(null)
  }

  const handleImport = async () => {
    if (!canImport()) return
    await run(async () => {
      if (csvRows.length > 0) {
        await handleCsvImport()
        return
      }
      const keyOf = (f: File): string => `${f.name}_${f.size}`
      const fileList = [...files]

      setFileStatuses(Object.fromEntries(fileList.map((f) => [keyOf(f), { status: 'pending' } as FileStatus])))
      setImportProgress({ current: 0, total: fileList.length })

      // 大量ファイル時の失敗を避けるため1ファイルずつ直列で送信する。
      // new モードでは最初のファイルで Product を作成し、以降は append で同じ Product へ追加する。
      let targetId: string | undefined = mode === 'append' ? targetProduct?.domain_product_id : undefined
      let productId: number | null = null
      let registered = 0
      let skipped = 0
      let failed = 0

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i]
        const key = keyOf(file)
        const fileMode: 'new' | 'append' = targetId ? 'append' : 'new'
        let capturedId: string | undefined

        const onMessage = (event: ImportEvent) => {
          if (event.status === 'start') {
            capturedId = event.domain_product_id
            productId = event.product_id
          } else if (event.status === 'file_done') {
            registered += 1
            setFileStatuses((prev) => ({ ...prev, [key]: { status: 'done' } }))
          } else if (event.status === 'file_skipped') {
            skipped += 1
            setFileStatuses((prev) => ({ ...prev, [key]: { status: 'skipped' } }))
          } else if (event.status === 'file_error') {
            failed += 1
            setFileStatuses((prev) => ({ ...prev, [key]: { status: 'error', message: event.message } }))
          } else if (event.status === 'done') {
            capturedId = event.domain_product_id
            productId = event.product_id
          } else if (event.status === 'error') {
            failed += 1
            setFileStatuses((prev) => ({ ...prev, [key]: { status: 'error', message: event.message } }))
          }
        }

        await streamImportUpload(
          {
            files: [file],
            domain,
            title: title.trim(),
            // タグ・サムネイルは最初の new リクエストでのみ適用する
            tags: fileMode === 'new' ? tags : [],
            mode: fileMode,
            targetDomainProductId: targetId,
            thumbnail: fileMode === 'new' ? thumbnail : null,
            thumbnailUrl: fileMode === 'new' ? (thumbnail ? null : thumbnailUrl) : null,
            releaseDate: releaseDate || null,
            description: description || null,
            // 図書館予約由来のインポートでは ISBN を domain_product_id に使う
            isbn: fileMode === 'new' ? libraryIsbn : null,
          },
          onMessage,
          (err) => {
            failed += 1
            setFileStatuses((prev) => ({ ...prev, [key]: { status: 'error', message: err.message } }))
          },
        )

        // 最初に作成された Product ID を以降の append で再利用する
        if (capturedId) targetId = capturedId
        setImportProgress({ current: i + 1, total: fileList.length })
      }

      toast.success(`登録 ${registered} / スキップ ${skipped} / 失敗 ${failed}`)

      // 失敗が無ければフォームをリセット（storeタグは維持）
      if (failed === 0) {
        await markLibraryBookReturned()
        await queueOcrForProduct(productId)
        setFiles([])
        setFileStatuses({})
        setTitle('')
        setReleaseDate('')
        setDescription('')
        setAmazonUrl('')
        setThumbnail(null)
        setThumbnailUrl(null)
        setTags((prev) => prev.filter((t) => t.type === 'store'))
        setLibraryIsbn(null)
      }

      setImportProgress(null)
    })
  }

  if (!hasFullAccess) return null

  return (
    <div className="min-h-screen w-full bg-background">
      <div className="sticky top-0 z-30">
        <Header title="インポート" onClose={() => navigate(PATHS.HOME)} />
      </div>
      <div className="w-full max-w-3xl mx-auto px-4 py-4 space-y-5">
        {/* ファイル選択 / DnD */}
        <div
          onDragOver={(e) => {
            e.preventDefault()
            setIsDragging(true)
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors ${
            isDragging ? 'border-primary bg-primary/10' : 'border-border'
          }`}
        >
          <Upload size={32} className="mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground mb-3">
            ファイルかフォルダをドラッグ&ドロップ（またはCSVで一括指定）
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={importing}>
              <FileUp size={16} className="mr-1" /> ファイル選択
            </Button>
            <Button variant="outline" onClick={() => dirInputRef.current?.click()} disabled={importing}>
              <FolderUp size={16} className="mr-1" /> フォルダ選択
            </Button>
            <Button variant="outline" onClick={() => csvInputRef.current?.click()} disabled={importing}>
              <FileSpreadsheet size={16} className="mr-1" /> CSV選択
            </Button>
          </div>
          <input ref={fileInputRef} type="file" multiple hidden onChange={handleFileInput} />
          <input ref={dirInputRef} type="file" multiple hidden onChange={handleFileInput} />
          <input ref={csvInputRef} type="file" accept=".csv,text/csv" hidden onChange={handleCsvInput} />
        </div>

        {/* CSV一覧（順番はソートせずCSVのまま登録） */}
        {csvRows.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                CSV: {csvName}（{csvRows.length}件）
              </span>
              <button
                onClick={() => {
                  setCsvRows([])
                  setCsvName('')
                }}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                disabled={importing}
              >
                <Trash2 size={12} /> クリア
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              CSVの順番でEpisodeを登録（2列目→カスタム名 / 3列目→アップロード日）
            </p>
            <div className="max-h-64 overflow-y-auto rounded-md border border-border divide-y divide-border">
              {csvRows.map((row, idx) => (
                <div key={`${row.path}_${idx}`} className="px-3 py-2 text-sm">
                  <p className="truncate">{row.customName || row.path.split('/').pop()}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {row.path}
                    {row.uploadDate ? ` ・ ${row.uploadDate}` : ''}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ファイル一覧（ドロップした結果がすぐ目に入るよう、ドロップ領域の直下に置く） */}
        {files.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm">
                <span className="font-medium">{files.length}件</span>
                <span className="text-muted-foreground"> / 不正拡張子 {invalidFiles.length}件</span>
              </span>
              <button
                onClick={() => setFiles([])}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                disabled={importing}
              >
                <Trash2 size={12} /> 全クリア
              </button>
            </div>
            <p className="text-xs text-muted-foreground">ドラッグで並び替え（上から順にindexを登録）</p>
            <div ref={fileListRef} className="max-h-64 overflow-y-auto rounded-md border border-border">
              <DndContext sensors={fileSensors} collisionDetection={closestCenter} onDragEnd={handleFileDragEnd}>
                <SortableContext items={files.map(fileKey)} strategy={verticalListSortingStrategy}>
                  <div className="divide-y divide-border">
                    {files.map((file) => {
                      const key = fileKey(file)
                      return (
                        <SortableFileItem
                          key={key}
                          file={file}
                          status={fileStatuses[key]}
                          valid={isExtAllowed(file.name)}
                          importing={importing}
                          isNew={recentKeys.includes(key)}
                          onRemove={removeFile}
                        />
                      )
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </div>
        )}

        {/* モード切替 */}
        {/* 図書館予約由来の選択中 (libraryIsbn) は new モード固定なので append への切替は無効化する */}
        <div className="flex gap-2">
          <Button
            variant={mode === 'new' ? 'default' : 'outline'}
            onClick={() => setMode('new')}
            className="flex-1"
            disabled={importing}
          >
            新規Product
          </Button>
          <Button
            variant={mode === 'append' ? 'default' : 'outline'}
            onClick={() => setMode('append')}
            className="flex-1"
            disabled={importing || libraryIsbn !== null}
          >
            既存Productへ追加
          </Button>
        </div>

        {/* ドメイン選択 */}
        <div className="space-y-1.5">
          <label className="text-sm text-muted-foreground">ドメイン</label>
          <Select value={domain} onValueChange={setDomain} disabled={importing || libraryIsbn !== null}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {IMPORTABLE_DOMAINS.map((d) => (
                <SelectItem key={d} value={d}>
                  {getDomainLabel(d)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {allowedExts && <p className="text-xs text-muted-foreground">許可拡張子: {allowedExts.join(', ')}</p>}

          {/* 図書館予約由来の選択状態バッジ */}
          {libraryIsbn !== null && (
            <div className="flex items-center justify-between rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-xs">
              <div className="flex items-center gap-2">
                <Library size={14} className="text-amber-500" />
                <span>図書館予約から選択中 (ISBN {libraryIsbn}) — ドメインは local に固定されます</span>
              </div>
              <button
                type="button"
                onClick={clearLibrarySelection}
                className="text-muted-foreground hover:text-foreground"
                disabled={importing}
              >
                解除
              </button>
            </div>
          )}

          {/* 図書館予約選択トリガー (新規モードのみ) */}
          {mode === 'new' && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setLibraryReservedOpen(true)}
              disabled={importing || libraryChecking}
              className="mt-1"
            >
              {libraryChecking ? (
                <Loader2 size={14} className="mr-1 animate-spin" />
              ) : (
                <Library size={14} className="mr-1" />
              )}
              図書館の予約から選択
            </Button>
          )}
        </div>

        {/* 新規モード: タイトル + タグ */}
        {mode === 'new' && (
          <div className="space-y-4">
            {/* 自動モード切替 */}
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
              <div className="flex flex-col">
                <span className="text-sm font-medium">自動モード</span>
                <span className="text-xs text-muted-foreground">
                  {autoSearching ? (
                    <span className="flex items-center gap-1">
                      <Loader2 size={12} className="animate-spin" /> Amazonを検索中...
                    </span>
                  ) : (
                    'ファイル選択時にファイル名でAmazon検索し、確認後にAI解析'
                  )}
                </span>
              </div>
              <Switch checked={autoMode} onCheckedChange={setAutoMode} disabled={importing} />
            </div>

            {/* Amazon URLからAI取得 */}
            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground">Amazon URL（AIでタグ・サムネイル自動取得）</label>
              <div className="flex gap-2">
                <Input
                  value={amazonUrl}
                  onChange={(e) => setAmazonUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleFetchAmazon()
                    }
                  }}
                  placeholder="https://www.amazon.co.jp/dp/..."
                  disabled={importing || fetchingMeta}
                />
                <Button
                  onClick={() => handleFetchAmazon()}
                  variant="outline"
                  className="shrink-0"
                  disabled={importing || fetchingMeta || !amazonUrl.trim()}
                >
                  {fetchingMeta ? (
                    <Loader2 size={16} className="mr-1 animate-spin" />
                  ) : (
                    <Sparkles size={16} className="mr-1" />
                  )}
                  AI取得
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground">タイトル</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Productタイトル"
                disabled={importing}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground">発売日（任意）</label>
              <Input
                type="date"
                value={releaseDate}
                onChange={(e) => setReleaseDate(e.target.value)}
                disabled={importing}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm text-muted-foreground">概要・説明（任意）</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="作品の概要・あらすじ"
                disabled={importing}
                rows={4}
                className="w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">タグ</label>
              <div className="flex gap-2">
                <Select value={newTagType} onValueChange={setNewTagType} disabled={importing}>
                  <SelectTrigger className="w-32 shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TAG_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addTag()
                    }
                  }}
                  placeholder="タグ名"
                  disabled={importing}
                />
                {tagNameSuggestions.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="shrink-0 px-3!"
                        disabled={importing}
                        aria-label="既存タグから選択"
                      >
                        <ChevronDown size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="max-h-64 overflow-y-auto">
                      {tagNameSuggestions.map((name) => (
                        <DropdownMenuItem key={name} onSelect={() => setNewTagName(name)}>
                          {name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <Button onClick={addTag} variant="outline" className="shrink-0 px-3!" disabled={importing}>
                  <Plus size={16} />
                </Button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag, i) => (
                    <Badge key={`${tag.type}_${tag.name}`} variant="secondary" className="gap-1">
                      {tag.type}: {tag.name}
                      <button onClick={() => removeTag(i)} aria-label="削除" disabled={importing}>
                        <X size={12} />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 既存モード: Product検索 */}
        {mode === 'append' && (
          <div className="space-y-2">
            <label className="text-sm text-muted-foreground">追加先Product</label>
            <div className="flex gap-2">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleSearch()
                  }
                }}
                placeholder="タイトルで検索"
                disabled={importing}
              />
              <Button onClick={handleSearch} variant="outline" className="shrink-0" disabled={importing}>
                検索
              </Button>
            </div>
            {targetProduct && (
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                <span>選択中: {targetProduct.title}</span>
                <button onClick={() => setTargetProduct(null)} aria-label="解除">
                  <X size={14} />
                </button>
              </div>
            )}
            {searchResults.length > 0 && !targetProduct && (
              <div className="max-h-48 overflow-y-auto rounded-md border border-border divide-y divide-border">
                {searchResults.map((p) => (
                  <button
                    key={p.domain_product_id}
                    onClick={() => {
                      setTargetProduct(p)
                      setSearchResults([])
                    }}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-accent/50"
                  >
                    {p.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* サムネイル（任意・タグの下） */}
        <div className="space-y-1.5">
          <label className="text-sm text-muted-foreground">サムネイル（任意・未指定時は自動抽出）</label>
          <Input
            type="file"
            accept="image/*"
            onChange={(e) => setThumbnail(e.target.files?.[0] ?? null)}
            disabled={importing}
          />
          {thumbnail && <p className="text-xs text-muted-foreground">{thumbnail.name}</p>}
          {!thumbnail && thumbnailUrl && (
            <div className="flex items-center gap-2">
              <img src={thumbnailUrl} alt="Amazonサムネイル" className="h-20 w-auto rounded border border-border" />
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">Amazonから取得した画像を使用</span>
                <button
                  onClick={() => setThumbnailUrl(null)}
                  className="text-xs text-muted-foreground hover:text-foreground self-start"
                  disabled={importing}
                >
                  クリア
                </button>
              </div>
            </div>
          )}
        </div>

        {/* インポート実行（常に下部に固定） */}
        <div className="sticky bottom-0 -mx-4 flex flex-col gap-2 border-t border-border bg-background px-4 py-3">
          {hasPdfTarget && (
            <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
              <div className="flex flex-col">
                <span className="text-sm font-medium">インポート後に再OCR</span>
                <span className="text-xs text-muted-foreground">
                  Yomitoku で PDF のテキストレイヤーを作り直す（キューで順番待ち）
                </span>
              </div>
              <Switch checked={runOcrAfterImport} onCheckedChange={setRunOcrAfterImport} disabled={importing} />
            </div>
          )}
          {importProgress && (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>登録中...</span>
                <span>
                  {importProgress.current} / {importProgress.total}
                </span>
              </div>
              <Progress value={importProgress.total > 0 ? (importProgress.current / importProgress.total) * 100 : 0} />
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClearAll} disabled={importing} className="shrink-0">
              <Trash2 size={16} className="mr-1" /> クリア
            </Button>
            <Button onClick={handleImport} disabled={!canImport()} className="flex-1">
              {importing ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" /> インポート中...
                </>
              ) : (
                <>
                  <Upload size={16} className="mr-2" /> インポート開始
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* 図書館の予約から選択 */}
      <LibraryReservedSheet
        open={libraryReservedOpen}
        onOpenChange={setLibraryReservedOpen}
        onSelect={handleLibrarySelect}
        checking={libraryChecking}
      />

      {/* 自動モード: Amazon検索結果の確認ダイアログ */}
      <AlertDialog open={autoConfirm !== null} onOpenChange={(open) => !open && setAutoConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>このAmazon商品でAI解析しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              「{autoConfirm?.keyword}」で検索した一番上の商品を外部ブラウザで開きました。
              この商品でメタデータをAI解析する場合はOKを押してください。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setAutoConfirm(null)}>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (autoConfirm) {
                  setAmazonUrl(autoConfirm.productUrl)
                  handleFetchAmazon(autoConfirm.productUrl)
                }
                setAutoConfirm(null)
              }}
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
